import React, { useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import { useAbundanceEngineParameters, useParameterReplacementEx } from '../../../hooks/useAbundanceEngineParameters'
import { useSearchClients } from '../../../api/clients'
import { useIntegrationAuthState } from '../IntegrationAuthProvider/IntegrationAuthContext'
import LoadingView from '../../pages/LoadingView'
import ErrorView from '../../pages/ErrorView'
import SydButton from '../../commonDesign/Button'
import { ICON_NAMES } from '../../../constants'
import AuthDialog from '../../pages/preferences/integrations/AuthDialog'

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden'
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block'
  }
}))

const IntegrationIframe = ({
  integration,
  baseUrl,
  params = {},
  className,
  title,
  sandbox = 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox',
  allow = 'fullscreen',
  width = '100%',
  height = '100%',
  allowAuth = true,
  authLabels = {
    connectButton: 'Get started',
    buttonIcon: ICON_NAMES.link,
    title: 'Authentication required',
    subtitle: 'Please connect your integration to continue.'
  },
  errorLabels = {
    title: 'Error loading data',
    subtitle: 'An error occurred while loading this data. Please try again later.'
  }
}) => {
  const classes = useStyles()
  const dialogRef = useRef(null)
  const abundanceParams = useAbundanceEngineParameters()

  const authState = useIntegrationAuthState(integration, {
    enabled: !!integration
  })

  const {
    isAuthenticated,
    isLoading: isLoadingAuth,
    requiresAuth,
    tokenData,
    authInputs,
    domainName,
    handleAuthSuccess
  } = authState

  const { data: clientContextData, isLoading: isLoadingClientContextData } = useSearchClients(
    {
      filters: { clientId: abundanceParams?.clientId },
      includes: { integrations: true }
    },
    { enabled: !!abundanceParams?.clientId, mapper: data => data?.data?.[0] || null }
  )

  const replacementData = useMemo(() => ({
    ...(tokenData || {}),
    ...(clientContextData ? {
      orionClientId: clientContextData.orionClientId,
      clientId: clientContextData.clientId
    } : {})
  }), [tokenData, clientContextData])

  const [resolvedParams, missingVariables] = useParameterReplacementEx(params, replacementData)

  const buildUrl = ({ path = '', params = {} }) => {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v != null)
    )
    const qs = new URLSearchParams(filtered).toString()
    return qs ? `${path}?${qs}` : path
  }

  const finalParams = useMemo(() => {
    return Object.entries(resolvedParams).reduce((acc, [key, value]) => {
      if (value !== undefined) {
        acc[key] = value?.path ? buildUrl(value) : value
      }
      return acc
    }, {})
  }, [resolvedParams])

  const handleConnectClick = useCallback(() => {
    if (dialogRef.current && integration && authInputs) {
      dialogRef.current.open(integration, authInputs, { domainName })
    }
  }, [integration, authInputs, domainName])

  const onComplete = useCallback(() => {
    handleAuthSuccess()
  }, [handleAuthSuccess])

  const iframeUrl = useMemo(() => {
    if (!baseUrl || !isAuthenticated) return null

    if (missingVariables && missingVariables.length > 0) {
      console.warn('IframeIntegration: Missing variables', missingVariables)
      return null
    }

    try {
      const url = new URL(baseUrl)
      if (finalParams) {
        Object.entries(finalParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            url.searchParams.set(key, value)
          }
        })
      }
      return url.toString()
    } catch (err) {
      console.error('Error constructing iframe URL:', err)
      return null
    }
  }, [baseUrl, isAuthenticated, finalParams, missingVariables])

  const isLoading = isLoadingAuth || isLoadingClientContextData
  if (isLoading) return <LoadingView width='100%' height='100%' />

  if (requiresAuth && allowAuth) {
    return (
      <div className='flex flex-col items-center justify-center' style={{ width, height }}>
        <div className='flex flex-col items-center justify-center gap-2'>
          <p className='font-size-2xl text-center mb-0'>
            {authLabels.title || 'Authentication Required'}
          </p>
          {authLabels.subtitle?.length && (
            <p className='font-size-md text-center mt-0 mb-4'>
              {authLabels.subtitle}
            </p>
          )}
          <SydButton
            size='lg'
            variant='primary'
            onClick={handleConnectClick}
            icon={authLabels.buttonIcon}
            disabled={!authInputs}
          >
            {authLabels.connectButton || 'Get Started'}
          </SydButton>
        </div>
        <AuthDialog ref={dialogRef} onComplete={onComplete} />
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <ErrorView
        error={{
          message: errorLabels.title,
          details: errorLabels.subtitle
        }}
      />
    )
  }

  if (!iframeUrl) {
    return (
      <ErrorView
        error={{
          message: 'Unable to construct integration URL. Please check your configuration.'
        }}
      />
    )
  }

  return (
    <div className={`${classes.container} ${className || ''}`} style={{ width, height }}>
      <iframe
        src={iframeUrl}
        className={classes.iframe}
        title={title || `${integration} integration`}
        sandbox={sandbox}
        allow={allow}
        referrerPolicy='strict-origin-when-cross-origin'
      />
    </div>
  )
}

IntegrationIframe.propTypes = {
  integration: PropTypes.string.isRequired,
  baseUrl: PropTypes.string.isRequired,
  params: PropTypes.object,
  className: PropTypes.string,
  title: PropTypes.string,
  sandbox: PropTypes.string,
  allow: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  allowAuth: PropTypes.bool,
  authLabels: PropTypes.shape({
    connectButton: PropTypes.string,
    buttonIcon: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string
  }),
  errorLabels: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string
  })
}

export default IntegrationIframe
